// Decode a `data:` URL into a Blob WITHOUT a network fetch.
//
// `fetch('data:...')` is the obvious way to turn a data URL into a Blob, but the
// app's CSP `connect-src` directive does NOT whitelist the `data:` scheme, so
// the browser blocks that fetch with "Refused to connect … violates the
// document's Content Security Policy" → "Failed to fetch". That silently broke
// group-avatar upload at create time (the upload threw, the group was created
// with no `avatarMediaId`, so no photo showed on any device).
//
// Decoding the base64 payload in-process sidesteps CSP entirely — no request is
// made. Mirrors the existing `base64ToBlob` helper but parses the full data URL.

/**
 * Parse a `data:[<mime>][;base64],<payload>` URL into a Blob.
 * Throws on a malformed input so callers can fall back (e.g. skip the avatar).
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const comma = dataUrl.indexOf(',');
  if (!dataUrl.startsWith('data:') || comma === -1) {
    throw new Error('not a data: URL');
  }
  const header = dataUrl.slice(5, comma); // between `data:` and `,`
  const isBase64 = /;base64/i.test(header);
  const mime = header.split(';')[0] || 'application/octet-stream';
  const payload = dataUrl.slice(comma + 1);
  if (isBase64) {
    const binary = atob(payload);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }
  // Non-base64 data URLs are percent-encoded text.
  return new Blob([decodeURIComponent(payload)], { type: mime });
}
